"use client";

import { cn } from "@/lib/utils";
import { Slot } from "@radix-ui/react-slot";
import { forwardRef } from "react";

type Variant = "primary" | "ghost" | "outline" | "surface" | "danger";
type Size = "sm" | "md" | "lg";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
}

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-cb-violet text-white hover:bg-cb-violet-dim active:scale-[0.98]",
  ghost:
    "text-cb-muted hover:text-cb-text hover:bg-cb-surface-2 active:scale-[0.98]",
  outline:
    "border border-cb-border text-cb-text hover:border-cb-violet hover:text-cb-violet-text active:scale-[0.98]",
  surface:
    "border border-cb-border bg-cb-surface text-cb-text hover:border-cb-violet/40 hover:bg-cb-surface-2 active:scale-[0.98]",
  danger:
    "bg-cb-danger/10 text-cb-danger border border-cb-danger/30 hover:bg-cb-danger/20 active:scale-[0.98]",
};

const sizeClasses: Record<Size, string> = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-5 py-2.5 text-sm",
  lg: "px-7 py-3.5 text-base",
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        ref={ref}
        className={cn(
          "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50 disabled:opacity-50 disabled:pointer-events-none cursor-pointer",
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button };
